"use client";

import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { useMemo, useState } from "react";
import { coverImageObjectPosition, coverImageSrc } from "@/lib/cover-image-meta";
import { EDITORIAL_LABELS } from "@/lib/editorial-labels";
import type { WorldCupTopArticle } from "@/lib/world-cup-data";

const PAGE_SIZE = 6;

export function WorldCupTopNewsSidebar({ articles }: { articles: WorldCupTopArticle[] }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(articles.length / PAGE_SIZE));
  const visible = useMemo(() => articles.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE), [articles, page]);

  return (
    <section className="rounded-md border border-[#E5DDD0] bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-[#E5DDD0] pb-3">
        <h2 className="font-display text-3xl font-bold leading-none text-[#111111]">{EDITORIAL_LABELS.worldCup.topNews}</h2>
        {articles.length > PAGE_SIZE ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              aria-label="Előző top hírek oldal"
              className="grid h-8 w-8 place-items-center rounded-full border border-[#D8C7AE] text-[#273E47] disabled:opacity-40"
              onClick={() => setPage((value) => Math.max(0, value - 1))}
              disabled={page === 0}
            >
              <ChevronLeft size={15} />
            </button>
            <button
              type="button"
              aria-label="Következő top hírek oldal"
              className="grid h-8 w-8 place-items-center rounded-full border border-[#D8C7AE] text-[#273E47] disabled:opacity-40"
              onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))}
              disabled={page >= pageCount - 1}
            >
              <ChevronRight size={15} />
            </button>
          </div>
        ) : null}
      </div>
      <div className="mt-2">
        {visible.length ? (
          visible.map((article, index) => (
            <Link
              key={article.id}
              href={`/article/${article.slug}`}
              className="group grid grid-cols-[28px_1fr_auto] gap-3 border-b border-[#EFE8DD] py-3 last:border-b-0"
            >
              <span className="font-display text-2xl font-bold leading-none text-[#A4243B]">
                {page * PAGE_SIZE + index + 1}
              </span>
              <span className="min-w-0">
                <span className="line-clamp-3 text-sm font-black leading-tight text-[#111111] group-hover:underline">
                  {article.title}
                </span>
                <span className="mt-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8B8175]">
                  <Eye size={12} />
                  {article.viewCount}
                </span>
              </span>
              {article.coverImage ? (
                <span className="relative h-12 w-14 overflow-hidden rounded-sm bg-[#EFE8DD]">
                  <Image
                    src={coverImageSrc(article.coverImage, article.coverImageMeta, "400")}
                    alt=""
                    fill
                    sizes="56px"
                    className="object-cover"
                    style={{ objectPosition: coverImageObjectPosition(article.coverImageMeta) }}
                  />
                </span>
              ) : null}
            </Link>
          ))
        ) : (
          <div className="py-4 text-sm font-semibold leading-5 text-[#6B6B6B]">
            Még nincs világbajnoksági anyag.
          </div>
        )}
      </div>
    </section>
  );
}
